import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ItemInput } from "../../../../@types";
import { ServiceData, ServicePriceData } from "../../../../@types/service";
import { createNewService } from "../../../../backend-services/service";
import { validateCreateService } from "../../../../modules/services/services";
import { useServiceStore } from "../../../../state/useServiceStore";
import { convertToFloat } from "../../../../utils/convertToFloat";
import ServiceForm from "../../../services/create/ServiceForm";
import ServiceInvoiceForm from "../../../services/create/ServiceInvoiceForm";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "../../../../assets/icons";
import { useCompanyStore } from "../../../../state/useCompanyStore";
import { useGetPaymentCurrencyList } from "../../../../utils";
import { showToast } from "../../../../utils/toastUtils";

interface CreateServiceProps {
  showBack?: boolean;
  onClose?: () => void;
  setShowCreateService: (show: boolean) => void;
  onCreateSuccess: (newItem: ItemInput) => void;
  handleCreateService: (e: React.FormEvent<HTMLFormElement>) => void;
  serviceType?: "time-based" | "session-based";
}

const ServiceOverlay = ({
  showBack,
  onClose,
  setShowCreateService,
  onCreateSuccess,
  serviceType,
}: CreateServiceProps) => {
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const userLocalCurrency = userCurrencyCode.currency;
  const userCountry = companyProfile.company.country === "US" ? "CAD" : "USD";
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [price, setPrice] = useState<ServicePriceData[]>([]);
  const [serviceData, setServiceData] = useState<ServiceData>({
    id: "",
    name: "",
    duration: "",
    description: "",
    price: [],
    status: "Active",
    serviceType: "time-based",
  });
  const [error, setError] = React.useState({
    name: "",
    charge: {
      [userLocalCurrency]: "",
      GBP: "",
      [userCountry]: "",
    },
  });
  const navigate = useNavigate();
  const { t } = useTranslation("services");
  const { createService } = useServiceStore((state) => ({
    createService: state.createService,
  }));

  const handleCreateService = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newPrice = price.map((item) => ({
      ...item,
      amount: convertToFloat(item.amount),
    }));

    const { hasErrors, errors } = validateCreateService({
      name: serviceData.name,
      duration: serviceData.duration,
      description: serviceData.description,
      price: newPrice,
      userLocalCurrency,
      userCountry,
      serviceType: serviceData.serviceType,
    });

    if (hasErrors) {
      setError(errors);
    } else {
      try {
        setIsLoading(true);

        let newServiceData = {
          ...serviceData,
          price: newPrice,
          serviceType: serviceData.serviceType,
        };
        if (!serviceData.duration) {
          const today = new Date();
          newServiceData = { ...newServiceData, duration: "0 hours" };
        }

        const response = await createNewService(newServiceData);
        showToast(t("service.successToast"), "success");
        setShowCreateService(false);
        setIsLoading(false);
        setServiceData({
          name: "",
          duration: "",
          description: "",
          price: [],
          status: "Active",
          serviceType: undefined,
        });

        onCreateSuccess(response);
      } catch (error) {
        showToast("Failed to create service.", "error");
        setIsLoading(false);
        console.error("Error creating service:", error);
      }
    }
  };

  return (
    <>
      <div className="mt-8  w-full">
        {showBack && (
          <div className="flex items-center gap-4">
            <h1 className="text-[18px] md:text-[20px] font-semibold lg:font-bold lg:hidden">
              Create Service
            </h1>
          </div>
        )}
        <div className="bg-[#FFFFFF]  pt-2 pb-[80px] md:pb-0  lg:mt-[-2.5em] md: mt-4">
          <div className="hidden lg:block">
            <div
              className="font-medium text-[24px]  lg:mt-4 flex justify-between"
              onClick={() => setShowCreateService(false)}
            >
              Create Service
              <button>
                <CloseIcon />
              </button>
            </div>
          </div>

          <ServiceInvoiceForm
            serviceData={serviceData}
            setServiceData={setServiceData}
            handleCreateService={handleCreateService}
            setPrice={setPrice}
            price={price}
            error={error}
            setError={setError}
            isLoading={isLoading}
            onClose={() => setShowCreateService(false)}
            serviceType={serviceType}
          />
        </div>
      </div>
    </>
  );
};

export default ServiceOverlay;
